import type { TowerDefinition, SendUnitDefinition, WaveConfig } from './types'

// Grid Configuration
export const GRID_WIDTH = 20
export const GRID_HEIGHT = 10
export const TILE_SIZE = 32

// Game Configuration
export const STARTING_GOLD = 500
export const STARTING_LIVES = 25
export const BASE_INCOME = 40
export const BUILD_TIME_SECONDS = 15
export const WAVE_END_PAUSE_MS = 2000
export const CREEP_SPAWN_INTERVAL_MS = 500
export const TICK_RATE_MS = 50

// Spawn and Exit positions
export const SPAWN_POSITION = { x: 0, y: 0 }
export const EXIT_POSITION = { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }

// Tower Definitions
export const TOWERS: Record<string, TowerDefinition> = {
  arrow: {
    type: 'arrow',
    cost: 50,
    damage: 10,
    range: 3,
    attackSpeed: 1.0,
    special: 'none'
  },
  cannon: {
    type: 'cannon',
    cost: 100,
    damage: 30,
    range: 2,
    attackSpeed: 0.5,
    special: 'splash',
    splashRadius: 1
  },
  frost: {
    type: 'frost',
    cost: 75,
    damage: 5,
    range: 3,
    attackSpeed: 0.8,
    special: 'slow',
    slowPercent: 30,
    slowDuration: 2000
  },
  sniper: {
    type: 'sniper',
    cost: 150,
    damage: 80,
    range: 5,
    attackSpeed: 0.3,
    special: 'none'
  }
}

// Send Unit Definitions
export const SEND_UNITS: Record<string, SendUnitDefinition> = {
  runner: {
    type: 'runner',
    cost: 25,
    hp: 30,
    speed: 1.5,
    incomeBonus: 1,
    count: 1
  },
  soldier: {
    type: 'soldier',
    cost: 50,
    hp: 80,
    speed: 1.0,
    incomeBonus: 2,
    count: 1
  },
  tank: {
    type: 'tank',
    cost: 100,
    hp: 200,
    speed: 0.7,
    incomeBonus: 4,
    count: 1
  },
  swarm: {
    type: 'swarm',
    cost: 75,
    hp: 20,
    speed: 1.2,
    incomeBonus: 3,
    count: 5
  }
}

// Wave Generation - Easier waves with better bounties
export function getWaveConfig(waveNumber: number): WaveConfig {
  const isBoss = waveNumber % 5 === 0
  const scaling = Math.pow(1.35, Math.floor(waveNumber / 5)) // Slower scaling

  if (isBoss) {
    return {
      wave: waveNumber,
      hp: Math.floor(400 * scaling),
      speed: 0.75,
      bounty: Math.floor(40 * scaling), // More bounty
      count: 3,
      isBoss: true
    }
  }

  const baseConfigs: WaveConfig[] = [
    { wave: 1, hp: 40, speed: 0.9, bounty: 8, count: 8, isBoss: false },
    { wave: 2, hp: 60, speed: 0.9, bounty: 10, count: 8, isBoss: false },
    { wave: 3, hp: 80, speed: 1.0, bounty: 12, count: 10, isBoss: false },
    { wave: 4, hp: 110, speed: 0.95, bounty: 14, count: 10, isBoss: false },
  ]

  const baseIndex = (waveNumber - 1) % 4
  const base = baseConfigs[baseIndex]

  return {
    wave: waveNumber,
    hp: Math.floor(base.hp * scaling),
    speed: base.speed,
    bounty: Math.floor(base.bounty * scaling),
    count: Math.floor(base.count + Math.floor(waveNumber / 2)), // Slower count increase
    isBoss: false
  }
}

// Colors for rendering
export const COLORS = {
  background: 0x1a1a2e,
  gridLine: 0x2d2d44,
  walkable: 0x16213e,
  blocked: 0x0f0f1a,
  spawn: 0x00ff00,
  exit: 0xff0000,
  towerArrow: 0x4a90d9,
  towerCannon: 0xd94a4a,
  towerFrost: 0x4ad9d9,
  towerSniper: 0xd9d94a,
  creepNormal: 0xe94560,
  creepSent: 0xff6b6b,
  creepBoss: 0x9b59b6,
  healthBarBg: 0x333333,
  healthBarFg: 0x00ff00,
  pathPreview: 0xffff00,
  invalidPlacement: 0xff0000,
  validPlacement: 0x00ff00
}
